// Angular
import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { map, catchError, of, switchMap, tap, withLatestFrom, EMPTY, mergeMap, retry, throwError, timer, Observable, filter, finalize, defer, ignoreElements, concat, debounceTime, startWith, distinctUntilChanged } from "rxjs";
// Local
import { AppState } from "../root";
import { PAGE_SIZE } from "../../constants/filter";
import { ToastService } from "../../services/toast/toast.service";
import { AlertActions, AlertLockActions } from "./alert.actions";
import { AuditActions } from "../audit/audit.actions";
import { AlertService, IAddCommentToAlertsRequest, IEscalateAlertsRequest, IMarkAlertsViewedRequest } from "../../services/alert/alert.service";
import { ANY_STATUS_FILTER, AlertFilterPreset, EmbeddingSearch, IAlertFilter, IFilterRequest, MAX_FILTER_WINDOW_MS, autoNamePreset, consumeTrackedSearch, createInitialFilter, loadCustomPresets, saveCustomPreset, withEmbeddingSearch } from "../../../modules/alerts/filter";
import { selectLoggedInUser, selectUser } from "../user/user.selectors";
import { ILoggedInUser, IUser, ToastType } from "../../models";
import {
  ALERT_VIDEO_AUTOPLAY_KEY,
  ALERT_VIDEO_OVERLAYS_KEY,
  DISMISS_ON_MUTE_KEY,
  IAlertPagination,
  IAlertState,
  KEYBOARD_MODE_KEY,
  loadPauseAfterAlertAction,
  storePauseAfterAlertAction,
  SIMILARITY_POLL_MS,
  WINDOW_SETTINGS_KEY
} from "./alert.states";
import { alertFilterToFlatFilters, applyDrilldownFilters, loadFilter, storeFilter } from "./helpers/filter.helpers";
import { getMaxPage } from "./helpers/alert.helpers";
import { IWindowSettings } from "./helpers/window.helpers";
import { AlertSocketService } from "../../services/alert/sockets.service";
import { IAlert, IAlertComment, IAlertDetection } from "../../models/alert.model";
import { IIdentityDetail } from "../../models/identity-detail.model";
import { IRegistryEntryPair, IResolveEntriesResponse } from "../../models/registry.interface";
import { RegistryService } from "../../services/registry/registry.service";
import { IDismissAlertsRequest } from "../../services/alert/alert.service";
import { DateTime } from "luxon";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import {
  selectAlerts,
  selectAlertsLoadedAt,
  selectAlertState,
  selectCurrentAlertState,
  selectFilter,
  selectStagedEmbeddingSearches,
  selectIsAlertsPageActive,
  selectIsFilterLoading,
  selectSimilarityFilterActive,
  selectLocks,
  selectLocksToRelease,
  selectPagination,
  selectPaused,
  selectSelectedAlerts,
  selectSocketConnected,
  selectviewTimestamp,
  selectWindow,
  selectWindowAlerts
} from "./alert.selectors";
import { recomputePresetWindow } from "../../components/date-range-picker/quick-date-range.interface";
import { mapScoutReviewFilterRequest } from "../../models/helpers/api-mappers";
import { selectDrilldownPath } from '../../../alert-dashboard/store/aggregate.selectors';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from "@ngrx/router-store";
import { Router } from "@angular/router";

// Pages whose alert stack is live — verifyAlertStack() only self-heals while one of these is open
function isAlertPageUrl(url: string): boolean {
  const alertPageUrls: string[] = ['/alerts', '/focus'];
  return alertPageUrls.some(page => url === page || url.startsWith(`${page}?`) || url.startsWith(`${page}/`));
}

// Unbounded, reversed, or multi-day windows are never auto-polled — a timer reissuing
// one heavy query forever is what turned a slow request into an outage. A null `to` is a
// rolling window that the request builder bounds at "now", so it is measured from `from` to now.
function isPollableWindow(from: Date | null | undefined, to: Date | null | undefined): boolean {
  if (from == null) {
    return false;
  }
  const duration = (to?.getTime() ?? Date.now()) - from.getTime();
  return duration >= 0 && duration <= MAX_FILTER_WINDOW_MS;
}

const RETRY_CONFIG = {
  count: 2,
  delay: (error: unknown): Observable<number> => {
    const delayMs = 1500;
    if (error instanceof HttpErrorResponse) {
      if (error.status >= 400) {
        return throwError(() => error);
      }
      return timer(delayMs);
    }
    return throwError(() => error);
  }
};

function isVideoWallOnlyUser(user: IUser): boolean {
  return user.roles?.length === 1 &&
    user.roles[0]?.name?.trim().toUpperCase() === "VIDEO WALL";
}

@Injectable()
export class AlertEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store<AppState>);
  private toastService = inject(ToastService);
  private alertService = inject(AlertService);
  private registryService = inject(RegistryService);
  private socketService = inject(AlertSocketService);
  private localStorageService = inject(LocalStorageService);
  private router = inject(Router);

  private loadAlertsInProgress = false;
  private bufferedAlerts: ({ id: string } & Partial<IAlert>)[] = [];

  private currentFilterPage: string | undefined;
  private seenAlertIds: string[] = [];

  loadAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.loadAlerts),
    filter(action => {
      if (!action.showLoading && this.loadAlertsInProgress) {
        return false;
      }
      return true;
    }),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectFilter),
      this.store$.select(selectPagination)
    ),
    // Silent polls must never run against an unbounded or multi-day window — a timer
    // reissuing one heavy query forever is what turned a slow request into an outage.
    filter(([_action, , filterData]) =>
      _action.showLoading || isPollableWindow(filterData.detection_time_from, filterData.detection_time_to)),
    switchMap(([_action, user, filterData, page]): Observable<Action> => {
      if (user == null) {
        return of(AlertActions.loadAlertsFailure({ error: new Error("Invalid user") }));
      }

      if (isVideoWallOnlyUser(user)) {
        return of(AlertActions.loadAlertsSuccess({
          alerts: [],
          total: 0,
          resetState: _action.showLoading
        }));
      }

      // A background refresh replaces the whole list, so it must re-fetch every page the
      // user has scrolled through — requesting only the current page would drop pages 1..N-1
      // (and show an empty grid once the current page runs past the result set).
      const effectivePage = _action.showLoading ? page : { ...page, current: 1 };
      const perPage = _action.showLoading ? PAGE_SIZE : page.current * PAGE_SIZE;
      return defer(() => {
        this.loadAlertsInProgress = true;
        return this.alertService.filterAlerts(user.organisation_id, effectivePage, filterData, perPage).pipe(
          withLatestFrom(this.store$.select(selectAlerts)),
          map(([response, currentAlerts]) => {
            const shouldFilterGhosts = !_action.showLoading;
            const seenInResponse = new Set<string>();
            const filtered = response.alerts.filter(a => {
              if (seenInResponse.has(a.id)) {
                return false;
              }
              seenInResponse.add(a.id);

              if (!shouldFilterGhosts) {
                return true;
              }
              const inStore = currentAlerts.items.some(x => x.id === a.id);
              if (inStore) {
                return true;
              }
              const alreadySeen = this.seenAlertIds.includes(a.id);
              return !alreadySeen;
            });
            const diff = response.alerts.length - filtered.length;
            const newTotal = Math.max(0, response.total - diff);
            return AlertActions.loadAlertsSuccess({ alerts: filtered, total: newTotal, resetState: _action.showLoading });
          }),
          retry(RETRY_CONFIG),
          catchError(error => of(AlertActions.loadAlertsFailure({ error }))),
          finalize(() => {
            this.loadAlertsInProgress = false;
          })
        );
      });
    })
  ));

  loadAlertPage$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.loadAlertPage),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectFilter),
      this.store$.select(selectPagination),
      this.store$.select(selectAlerts)
    ),
    switchMap(([action, user, filter, page, alerts]) => {
      if (user == null) {
        return of(AlertActions.loadAlertPageFailure({ error: Error("Invalid user") }))
      }

      if (isVideoWallOnlyUser(user)) {
        return of(AlertActions.loadAlertPageSuccess({
          alerts: [],
          newTotal: 0,
          newPage: 1
        }));
      }

      const maxPage = getMaxPage(alerts.total ?? 0);
      const clampedPage = Math.min(Math.max(1, action.page), maxPage);
      const newPage: IAlertPagination = {
        ...page,
        current: clampedPage
      };
      return this.alertService.filterAlerts(user.organisation_id, newPage, filter).pipe(
        withLatestFrom(this.store$.select(selectAlerts)),
        map(([response, currentAlerts]) => {
          const uniqueAlerts = Array.from(new Map(response.alerts.map(a => [a.id, a])).values());
          const currentAlertIds = new Set(currentAlerts.items.map(a => a.id));
          let ghostCount = 0;
          const filtered = uniqueAlerts.filter(a => {
            if (currentAlertIds.has(a.id)) {
              return true;
            }
            if (this.seenAlertIds.includes(a.id)) {
              ghostCount++;
              return false;
            }
            return true;
          });
          const newTotal = Math.max(0, response.total - ghostCount);
          return AlertActions.loadAlertPageSuccess({ alerts: filtered, newTotal, newPage: newPage.current });
        }),
        catchError(error => {
          const actionsToDispatch: Action[] = [AlertActions.loadAlertPageFailure({ error })];
          if (action.page > 1) {
            actionsToDispatch.push(AlertActions.loadAlertPage({ page: 1 }));
          }
          return of(...actionsToDispatch);
        })
      );
    })
  ));

  nextWindowNavigate$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.moveAlertWindow),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectPagination),
      this.store$.select(selectAlerts),
    ),
    mergeMap(([, window, pagination, alerts]) => {
      const validPagination = alerts.total > (pagination.current + 1) * PAGE_SIZE && !pagination.loading;
      const index = alerts.items.findIndex(() => window.currentAlertId)
      if (index > alerts.items.length - window.size * 2 && validPagination) {
        return of(AlertActions.loadAlertPage({ page: pagination.current + 1 }))
      }
      return EMPTY
    })
  ));

  markAlertsAsFalseAlarms$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.markAlertsAsFalseAlarm),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.markAlertsAsFalseAlarmFailure({ alerts: action.alerts, error: Error("Invalid user") }))
      }
      const request: IAddCommentToAlertsRequest = {
        user_id: user.id,
        username: user.name,
        alert_ids: action.alerts.map(e => e.id),
        text: "False Alarm",
        should_dismiss_alert: true
      }
      return this.alertService.addCommentToAlerts(request).pipe(
        map(() => {
          return AlertActions.markAlertsAsFalseAlarmSuccess()
        }),
        catchError(error => {
          this.toastService.showToast("Failed to mark as false alarm", ToastType.ERROR)
          return of(AlertActions.markAlertsAsFalseAlarmFailure({ alerts: action.alerts, error }))
        })
      );
    })
  ));

  dismissSelectedAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.dismissSelectedAlerts),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectWindowAlerts),
      this.store$.select(selectUser),
    ),
    debounceTime(200),
    mergeMap(([, window, alerts, user]) => {
      if (user == null) {
        return of(AlertActions.dismissAlertsFailure({ alerts: alerts, error: Error("Invalid user") }));
      }

      const alertsToDismiss = alerts.filter((alert: IAlert) => window.selected.includes(alert.id));
      return of(AlertActions._dismissAlerts({ alerts: alertsToDismiss, userID: user.id }));
    })
  ));

  _dismissAlertsWithRelease$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions._dismissAlerts),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectWindowAlerts),
      this.store$.select(selectUser),
      this.store$.select(selectviewTimestamp),
    ),
    mergeMap(([action, , , , viewTimestamp]) => {
      const alertIDs = action.alerts.map(e => e.id);
      const dismissTimestamp = DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');
      const dismissRequest: IDismissAlertsRequest = {
        userId: action.userID,
        alertIds: alertIDs,
        dismissTimestamp: dismissTimestamp
      };
      const alertViewTimestamp = viewTimestamp != undefined ? viewTimestamp[alertIDs[0]] : dismissTimestamp;

      if (viewTimestamp != undefined && viewTimestamp[alertIDs[0]] == undefined) {
        console.warn('View timestamp not set, defaulting to dismiss timestamp');
      }

      return this.alertService.dismissUsingUpdate(dismissRequest, alertViewTimestamp).pipe(
        map(() => AlertActions.clearAlertViewTimestamps({ alertIds: alertIDs })),
        catchError(error => {
          this.toastService.showToast("Failed to dismiss", ToastType.ERROR);
          return of(AlertActions.dismissAlertsFailure({ alerts: action.alerts, error }));
        })
      );
    })
  ));

  clearAlertViewTimestamps$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.clearAlertViewTimestamps),
    mergeMap((action) => {
      return of(AlertLockActions.releaseLocks({ ids: action.alertIds }));
    })
  ));

  dismissFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.dismissFilter),
    withLatestFrom(
      this.store$.select(selectFilter),
      this.store$.select(selectSelectedAlerts),
      this.store$.select(selectDrilldownPath),
    ),
    mergeMap(([, filter, selected, drilldownPath]) => {

      let currentfilter: IFilterRequest = alertFilterToFlatFilters(filter)

      if (selected.allSelected && selected.deselected.size > 0) {
        currentfilter = [
          ...currentfilter,
          { key: "id", operator: "NOT IN", value: [...selected.deselected.values()] },
        ]
      } else if (!selected.allSelected && selected.selected.size > 0) {
        currentfilter = [
          { key: "id", operator: "IN", value: [...selected.selected.values()] },
        ]
      }
      if (selected.allSelected) {
        currentfilter = applyDrilldownFilters(currentfilter, drilldownPath);
      }
      return this.alertService.dismissByFilter(currentfilter).pipe(
        map(() => {
          this.toastService.showToast("Dismissed successfully", ToastType.SUCCESS)
          return AlertActions.dismissFilterSuccess()
        }),
        catchError(error => {
          this.toastService.showToast("Failed to dismiss", ToastType.ERROR)
          return of(AlertActions.dismissFilterFailure({ error }))
        })
      );
    })
  ));

  actionFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.escalateFilterSuccess, AlertActions.dismissFilterSuccess),
    switchMap(() => {
      // Force a refresh for bulk actions
      return of(AlertActions.loadAlerts({ showLoading: false }))
    })
  ));

  escalateFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.escalateFilter),
    withLatestFrom(
      this.store$.select(selectFilter),
      this.store$.select(selectSelectedAlerts),
      this.store$.select(selectDrilldownPath),
    ),
    switchMap(([, filter, selected, drilldownPath]) => {
      let currentfilter: IFilterRequest = alertFilterToFlatFilters(filter)

      if (selected.allSelected && selected.deselected.size > 0) {
        currentfilter = [
          ...currentfilter,
          { key: "id", operator: "NOT IN", value: [...selected.deselected.values()] },
        ]
      } else if (!selected.allSelected && selected.selected.size > 0) {
        currentfilter = [
          { key: "id", operator: "IN", value: [...selected.selected.values()] },
        ]
      }
      if (selected.allSelected) {
        currentfilter = applyDrilldownFilters(currentfilter, drilldownPath);
      }
      return this.alertService.escalateByFilter(currentfilter).pipe(
        map(() => {
          this.toastService.showToast("Escalated successfully", ToastType.SUCCESS)
          return AlertActions.escalateFilterSuccess()
        }),
        catchError(error => {
          this.toastService.showToast("Failed to escalate", ToastType.ERROR)
          return of(AlertActions.escalateFilterFailure({ error }))
        })
      );
    })
  ));

  scoutReviewFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.scoutReviewFilter),
    withLatestFrom(
      this.store$.select(selectFilter),
      this.store$.select(selectSelectedAlerts),
      this.store$.select(selectDrilldownPath),
    ),
    mergeMap(([action, filter, selected, drilldownPath]) => {
      let currentfilter: IFilterRequest = alertFilterToFlatFilters(filter)

      if (selected.allSelected && selected.deselected.size > 0) {
        currentfilter = [
          ...currentfilter,
          { key: "id", operator: "NOT IN", value: [...selected.deselected.values()] },
        ]
      } else if (!selected.allSelected && selected.selected.size > 0) {
        currentfilter = [
          { key: "id", operator: "IN", value: [...selected.selected.values()] },
        ]
      }
      if (selected.allSelected) {
        currentfilter = applyDrilldownFilters(currentfilter, drilldownPath);
      }
      return this.alertService.scoutReviewFilter(mapScoutReviewFilterRequest(action, currentfilter)).pipe(
        map(() => {
          this.toastService.showToast("Scout is reviewing alerts", ToastType.SUCCESS)
          return AlertActions.scoutReviewFilterSuccess()
        }),
        catchError(error => {
          this.toastService.showToast("Failed to initiate Scout Review", ToastType.ERROR)
          return of(AlertActions.scoutReviewFilterFailure({ error }))
        })
      );
    })
  ));

  dismissAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.dismissAlerts),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectFilter),
    ),
    mergeMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.dismissAlertsFailure({ alerts: action.alerts, error: Error("Invalid user") }))
      }

      const dismissRequest: IDismissAlertsRequest = {
        userId: user.id,
        alertIds: action.alerts.map(e => e.id),
        dismissTimestamp: DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss')
      };

      return this.alertService.dismiss(dismissRequest).pipe(
        // This too is an anti-pattern
        ignoreElements(), // ignoreElements() prevents any success value from being emitted as an action.
        catchError(error => {
          this.toastService.showToast("Failed to dismiss", ToastType.ERROR);
          return of(AlertActions.dismissAlertsFailure({ alerts: action.alerts, error }));
        })
      );
    })
  ));

  dismissAlertsEarlySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.dismissAlerts),
    withLatestFrom(
      this.store$.select(selectUser),
    ),
    mergeMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.dismissAlertsFailure({ alerts: action.alerts, error: Error("Invalid user") }))
      }

      return of(AlertActions.dismissAlertsSuccess({ alerts: action.alerts, userID: user.id }));
    })
  ));

  escalateSelectedAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.escalateSelectedAlerts),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectWindowAlerts),
      this.store$.select(selectUser),
    ),
    mergeMap(([action, window, alerts, user]) => {
      const alertsToEscalate = alerts.filter((alert: IAlert) => window.selected.includes(alert.id));
      if (user == null) {
        return of(AlertActions.escalateAlertsFailure({ alerts: alertsToEscalate, error: Error("Invalid user") }))
      }
      return of(AlertActions._escalateAlerts({ alerts: alertsToEscalate, userID: user.id, comment: action.comment }))
    })
  ));

  _escalateAlertsWithRelease$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions._escalateAlerts),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectWindowAlerts),
      this.store$.select(selectLoggedInUser),
      this.store$.select(selectviewTimestamp),
    ),
    mergeMap(([action, , , user, viewTimestamp]) => {
      const alertIDs = action.alerts.map(e => e.id);
      const request: IEscalateAlertsRequest = {
        user_id: action.userID,
        username: user?.username,
        alert_ids: action.alerts.map(e => e.id),
        comment: action.comment
      }

      const alertViewTimestamp = viewTimestamp != undefined ? viewTimestamp[alertIDs[0]] : DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');

      if (viewTimestamp != undefined && viewTimestamp[alertIDs[0]] == undefined) {
        console.warn('View timestamp not set, defaulting current timestamp');
      }

      return this.alertService.escalate(request, alertViewTimestamp).pipe(
        map(() => AlertLockActions.releaseLocks({ ids: alertIDs })),
        catchError(error => {
          this.toastService.showToast("Failed to escalate", ToastType.ERROR)
          return of(AlertActions.escalateAlertsFailure({ alerts: action.alerts, error }))
        }),
      );
    })
  ));

  escalateAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.escalateAlerts),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectviewTimestamp),
    ),
    mergeMap(([action, user, viewTimestamp]) => {
      if (user == null) {
        return of(AlertActions.escalateAlertsFailure({ alerts: action.alerts, error: Error("Invalid user") }))
      }
      const alertIds = action.alerts.map(e => e.id);
      const request: IEscalateAlertsRequest = {
        user_id: user.id,
        username: user.username,
        alert_ids: alertIds,
        comment: action.comment
      }

      const alertViewTimestamp = viewTimestamp != undefined ? viewTimestamp[alertIds[0]] : DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');

      if (viewTimestamp != undefined && viewTimestamp[alertIds[0]] == undefined) {
        console.warn('View timestamp not set, defaulting current timestamp');
      }

      return this.alertService.escalate(request, alertViewTimestamp).pipe(
        ignoreElements(), // ignoreElements() prevents any success value from being emitted as an action.
        catchError(error => {
          this.toastService.showToast("Failed to escalate", ToastType.ERROR)
          return of(AlertActions.escalateAlertsFailure({ alerts: action.alerts, error }))
        })
      );
    })
  ));

  escalateAlertsEarlySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.escalateAlerts),
    withLatestFrom(this.store$.select(selectUser)),
    mergeMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.escalateAlertsFailure({ alerts: action.alerts, error: Error("Invalid user") }))
      }
      return of(AlertActions.escalateAlertsSuccess({ alerts: action.alerts, userID: user.id }));
    })
  ));

  setFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setFilter),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([action, user]) => {
      storeFilter(user!, action.filter, action.page)
      return of(AlertActions.loadAlerts({ showLoading: action.showLoading }))
    })
  ));


  // Tracking an object applies the filter immediately, widening status to All so
  // matches aren't hidden by the current triage-state preset. Tracks are persisted
  // to a single custom preset: the first track auto-creates it, subsequent tracks
  // add to it.
  applyEmbeddingSearch$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.addEmbeddingSearch),
    withLatestFrom(this.store$.select(selectFilter), this.store$.select(selectUser)),
    mergeMap(([action, currentFilter, user]): Observable<Action> => {
      // Presets are keyed by user; without one there is nowhere to persist the track.
      if (user == null) {
        return of();
      }
      const widened: IAlertFilter = { ...currentFilter, ...ANY_STATUS_FILTER, status_filter_logic: 'AND' };
      const customs = loadCustomPresets(user);
      const preset = customs[0];
      if (preset == null) {
        return this.createTrackPreset(user, widened, customs);
      }
      const search: EmbeddingSearch = {
        alertId: action.alertId,
        detectionId: action.detectionId,
        similarity: action.similarity,
        detectionLabel: action.detectionLabel,
        cameraLabel: action.cameraLabel,
        timeLabel: action.timeLabel,
        imageUrl: action.imageUrl,
        boundingBox: action.boundingBox,
        negative: action.negative,
      };
      const merged = withEmbeddingSearch(widened, search);
      saveCustomPreset(user, { ...preset, filter: { ...preset.filter, embedding_searches: merged.embedding_searches } });
      return of(
        AlertActions.trackPresetSaved({ presetId: preset.id }),
        this.setFilterAction(merged),
      );
    })
  ));

  // Applies every staged Good/Bad Match in a single filter update, so several
  // detections can be marked (e.g. multiple people in one image) before the
  // search reruns once.
  applyStagedEmbeddingSearches$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.applyStagedEmbeddingSearches),
    withLatestFrom(this.store$.select(selectFilter), this.store$.select(selectStagedEmbeddingSearches), this.store$.select(selectUser)),
    mergeMap(([, currentFilter, staged, user]): Observable<Action> => {
      // Presets are keyed by user; without one there is nowhere to persist the tracks.
      if (staged.length === 0 || user == null) {
        return of(AlertActions.clearStagedEmbeddingSearches());
      }
      try {
        const widened: IAlertFilter = { ...currentFilter, ...ANY_STATUS_FILTER, status_filter_logic: 'AND' };
        const merged = staged.reduce((f, search) => withEmbeddingSearch(f, search), widened);
        const customs = loadCustomPresets(user);
        const preset = customs[0];
        if (preset == null) {
          return concat(
            this.createTrackPreset(user, merged, customs),
            of(AlertActions.clearStagedEmbeddingSearches()),
          );
        }
        saveCustomPreset(user, { ...preset, filter: { ...preset.filter, embedding_searches: merged.embedding_searches } });
        return of(
          AlertActions.trackPresetSaved({ presetId: preset.id }),
          this.setFilterAction(merged),
          AlertActions.clearStagedEmbeddingSearches(),
        );
      } catch {
        this.toastService.showToast("Failed to apply staged embedding searches", ToastType.ERROR);
        return of(AlertActions.clearStagedEmbeddingSearches());
      }
    })
  ));

  private setFilterAction(filter: IAlertFilter): Action {
    return AlertActions.setFilter({ filter, showLoading: true, page: this.currentFilterPage! });
  }

  private createTrackPreset(user: ILoggedInUser, widened: IAlertFilter, customs: AlertFilterPreset[]): Observable<Action> {
    const preset: AlertFilterPreset = {
      id: crypto.randomUUID(),
      label: autoNamePreset(customs),
      filter: {
        status_conditions: widened.status_conditions,
        status_filter_logic: widened.status_filter_logic,
        embedding_searches: widened.embedding_searches,
      },
      groupBy: [],
      isCustom: true,
      axis: 'status',
    };
    saveCustomPreset(user, preset);
    return of(
      AlertActions.trackPresetSaved({ presetId: preset.id }),
      this.setFilterAction(widened),
    );
  }

  // The socket ignores alert mutations while a similarity filter is active (matching
  // requires a server-side embedding query), so poll for new matches instead. The
  // schedule is anchored to the last successful load so a manual refresh resets it
  // and the filter bar can show a countdown to the next poll.
  pollSimilarityAlerts$ = createEffect(() => timer(1000, 1000).pipe(
    withLatestFrom(
      this.store$.select(selectSimilarityFilterActive),
      this.store$.select(selectIsAlertsPageActive),
      this.store$.select(selectIsFilterLoading),
      this.store$.select(selectAlertsLoadedAt),
      this.store$.select(selectFilter),
    ),
    // Gate before dispatching: loadAlerts$ drops unpollable silent loads, so without this
    // the timer would emit a doomed action every second once the window goes stale.
    filter(([, similarityActive, pageActive, loading, loadedAt, currentFilter]) =>
      similarityActive && pageActive && !loading && Date.now() - loadedAt >= SIMILARITY_POLL_MS
      && isPollableWindow(currentFilter.detection_time_from, currentFilter.detection_time_to)),
    map(() => AlertActions.loadAlerts({ showLoading: false }))
  ));

  // Seeded with the current URL because ROUTER_NAVIGATED fires after the page component's
  // constructor has already run, which would otherwise leave the flag false on first load.
  setAlertsPageActive$ = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    map((action: RouterNavigatedAction) => isAlertPageUrl(action.payload.event.urlAfterRedirects)),
    startWith(isAlertPageUrl(this.router.url)),
    distinctUntilChanged(),
    map(active => AlertActions.setAlertsPageActive({ active }))
  ));

  loadFilter$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.loadFilter),
    withLatestFrom(this.store$.select(selectUser), this.store$.select(selectFilter)),
    mergeMap(([_action, user, stateFilter]) => {
      this.currentFilterPage = _action.page
      const forceDefault = user != null && !user.isAdmin;
      const currentFilter = forceDefault
        ? createInitialFilter(_action.page)
        : loadFilter(user!, _action.page)
      // A tracked-object search seeded via query param (opened from another window) takes
      // priority: merge it into the stored (or current) filter and strip the param.
      const tracked = consumeTrackedSearch()
      if (tracked != null) {
        window.history.replaceState(null, '', window.location.pathname)
        const base = currentFilter ?? stateFilter
        // addEmbeddingSearch routes the seeded track through the preset workflow.
        return of(
          AlertActions.setFilter({ filter: base, showLoading: true, page: _action.page }),
          AlertActions.addEmbeddingSearch({ ...tracked }),
        )
      }
      if (currentFilter != null) {
        return of(AlertActions.setFilter({ filter: currentFilter, showLoading: true, page: _action.page }))
      }
      return EMPTY
    })
  )
  );

  // Re-checks every minute and rolls a relative date preset's window forward when it drifts (the
  // minute, hour or day boundary passed). The reload is silent: rolling the window is not a user
  // action, so it must not clear the grid, show a spinner, reset the page or drop selections.
  refreshRelativePreset$ = createEffect(() => timer(0, 60000).pipe(
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectFilter),
      this.store$.select(selectIsAlertsPageActive),
    ),
    map(([, user, filter, pageActive]): IAlertFilter | null => {
      // currentFilterPage is set by loadFilter$; the startup tick can fire before it, and
      // dispatching with an undefined page would persist under the wrong storage key.
      if (!user || !pageActive || this.currentFilterPage == null) {
        return null;
      }
      const presetName = filter.selected_date_preset;
      const presetWindow = presetName ? recomputePresetWindow(presetName) : null;
      if (!presetWindow) {
        return null;
      }
      if (!isPollableWindow(presetWindow.start, presetWindow.end)) {
        return null;
      }
      // Compare exact timestamps, not just the day — otherwise sub-day presets like "Last Hour"
      // only refresh at midnight. Rolling presets snap to the minute so this only differs when the
      // window has actually moved.
      const startMatches = (filter.detection_time_from?.getTime() ?? null) === (presetWindow.start?.getTime() ?? null);
      const endMatches = (filter.detection_time_to?.getTime() ?? null) === (presetWindow.end?.getTime() ?? null);
      if (startMatches && endMatches) {
        return null;
      }
      return { ...filter, detection_time_from: presetWindow.start, detection_time_to: presetWindow.end };
    }),
    filter((updatedFilter): updatedFilter is IAlertFilter => updatedFilter != null),
    map((updatedFilter) => AlertActions.setFilter({ filter: updatedFilter, showLoading: false, page: this.currentFilterPage! }))
  ));

  updateAlert$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.updateAlert),
    withLatestFrom(
      this.store$.select(selectIsFilterLoading),
      this.store$.select(selectAlertState)
    ),
    mergeMap(([action, isLoading, alertState]) => {
      this.bufferedAlerts.push(action.alert);

      if (isLoading || alertState.alerts.items.length === 0) {
        return EMPTY;
      }

      const allStoreAlertIDs = new Set<string>(alertState.alerts.items.map(e => e.id));

      const validBufferedAlerts: ({ id: string } & Partial<IAlert>)[] = [];
      const leftoverBufferedAlerts: ({ id: string } & Partial<IAlert>)[] = [];

      for (const alert of this.bufferedAlerts) {
        if (allStoreAlertIDs.has(alert.id)) {
          validBufferedAlerts.push(alert);
        } else {
          leftoverBufferedAlerts.push(alert);
        }
      }

      this.bufferedAlerts = leftoverBufferedAlerts;

      if (validBufferedAlerts.length === 0) {
        return EMPTY;
      }

      return of(AlertActions.updateAlerts({ alerts: validBufferedAlerts }));
    })
  ));

  investigateAlert$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.investigateAlert),
    withLatestFrom(this.store$.select(selectUser)),
    mergeMap(([{ alert }, user]) => {
      if (user == null) {
        return of(AuditActions.addAuditEntryFailure({ error: Error("Invalid user") }));
      }
      return of(
        AuditActions.addAuditEntry({
          auditRequest: {
            action: 'EXECUTED',
            user_id: user.id,
            item_id: alert.id,
            table_name: 'ALERT',
            message: `EXECUTED Investigate on Alert - ${alert.camera_name}`,
          }
        }),
        AlertActions.commentOnAlerts({ alerts: [alert], comment: 'Investigated recordings' })
      );
    })
  ));

  callSiteContact$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.callSiteContact),
    withLatestFrom(this.store$.select(selectUser)),
    mergeMap(([{ alert, site }, user]) => {
      if (user == null) {
        return EMPTY;
      }
      const auditEntry = AuditActions.addAuditEntry({
        auditRequest: {
          action: 'CALLED',
          user_id: user.id,
          organisation_id: site.organisation_id,
          item_id: site.id,
          table_name: 'SITE',
          message: `CALLED Site Contact - ${site.name} (${site.contact_number})`,
        }
      });
      return of(
        auditEntry,
        AlertActions.commentOnAlerts({ alerts: [alert], comment: `Called site contact ${site.contact_number}` })
      );
    })
  ));

  addComments$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.commentOnAlerts),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.commentOnAlertsFailure({ error: Error("Invalid user") }))
      }
      const request: IAddCommentToAlertsRequest = {
        user_id: user.id,
        username: user.username,
        alert_ids: action.alerts.map(e => e.id),
        text: action.comment,
        should_dismiss_alert: false
      }
      return this.alertService.addCommentToAlerts(request).pipe(
        map(response => {
          const comments: IAlertComment[] = response.ids.map((id, index) => ({
            alert_id: id,
            id: response.comment_ids != null ? response.comment_ids[index] : undefined,
            author_name: response.name,
            author_id: response.user_id,
            text: response.text,
            timestamp: response.timestamp
          }));

          this.toastService.showToast("Comments added successfully", ToastType.SUCCESS);
          return AlertActions.commentOnAlertsSuccess({ comments: comments });
        }),
        catchError(error => {
          this.toastService.showToast("Failed to add comment on alerts", ToastType.ERROR)
          return of(AlertActions.commentOnAlertsFailure({ error }))
        })
      );
    })
  ));

  markAlertsViewed$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.markAlertsViewed),
    withLatestFrom(this.store$.select(selectLoggedInUser)),
    mergeMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.markAlertsViewedFailure({ error: Error("Invalid user") }))
      }
      const actionTimestamp = DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');
      const request: IMarkAlertsViewedRequest = {
        ids: action.alertIDs,
        user_id: user.id,
        timestamp: actionTimestamp,
        update_map: {
          viewed: true,
          viewed_by: user.id,
          view_timestamp: actionTimestamp
        }
      }
      return this.alertService.markAlertsViewed(request).pipe(
        mergeMap(() => [
          AlertActions.updateAlerts({ alerts: action.alertIDs.map(id => ({ id, viewed: true, viewed_by: user.id, view_timestamp: actionTimestamp })) }),
          AlertActions.markAlertsViewedSuccess()
        ]),
        catchError(error => {
          return of(AlertActions.markAlertsViewedFailure({ error }))
        })
      );
    })
  ))

  muteAllActiveAlerts$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.muteAllActiveAlerts),
    withLatestFrom(this.store$.select(selectUser)),
    mergeMap(([action, user]) => {
      if (user == null) {
        return of(AlertActions.muteAllActiveAlertsFailure({ error: Error("Invalid user") }))
      }
      return this.alertService.muteAllActiveAlerts(action.cameraIDs, action.reason).pipe(
        mergeMap((ids: string[]) => {
          const updateAlertActions = ids.map((id) => {
            const mute_data: { id: string } & Partial<IAlert> = {
              id: id,
              muted: true,
              muted_by: user.id,
              mute_timestamp: new Date().toISOString()
            };
            return AlertActions.updateAlert({ alert: mute_data });
          });
          return concat(
            ...updateAlertActions.map(action => of(action)),
            of(AlertActions.muteAllActiveAlertsSuccess())
          );
        }),
        catchError(error => {
          return of(AlertActions.muteAllActiveAlertsFailure({ error }))
        })
      );
    })
  ))

  loadAlertsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.loadAlertsSuccess),
    withLatestFrom(
      this.store$.select(selectWindow),
      this.store$.select(selectSocketConnected),
      this.store$.select(selectPaused),
    ),
    switchMap(([, window, socketConnected, paused]) => {
      if (!socketConnected && window.alerts.length <= 0 && !paused) {
        return of(AlertLockActions.setWindowWithoutConnection());
      }
      return EMPTY;
    })
  ))

  acquireNewLocks$ = createEffect(() => this.actions$.pipe(
    ofType(AlertLockActions.acquireNewLocks),
    debounceTime(200),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectCurrentAlertState),
      this.store$.select(selectLocksToRelease),
    ),
    tap(([, user, alertState]) => {
      if (user == null) {
        return
      }

      if (!alertState.window.canAcquireLocks) {
        return;
      }

      const lockMap = new Map(Object.entries(alertState.locks));
      let userHasLocks = false;
      lockMap.forEach((lock) => {
        if (lock.userID == user.id) {
          userHasLocks = true;
        }
      })

      if (userHasLocks && alertState.window.alerts.length > 0) {
        this.store$.dispatch(AlertLockActions.toggleRequestingNextLock({ requestLock: false }));
        return;
      }

      const alertLockRequest = {
        alert_ids: alertState.window.windows,
        group_index: alertState.paused.paused ? -1 : alertState.window.currentWindowIndex,
        user: {
          id: user.id,
          username: user.username
        }
      };


      // NOTE: Removing to assist with lock collisions
      // this.store$.dispatch(AlertActions.releaseLocks({ ids: locksToRelease }));
      this.socketService.requestAlertLocks(alertLockRequest);
    })
  ), { dispatch: false });

  acquireOtherLocks$ = createEffect(() => this.actions$.pipe(
    ofType(AlertLockActions.acquireOtherLocks),
    withLatestFrom(
      this.store$.select(selectUser),
      this.store$.select(selectCurrentAlertState)),
    tap(([, user, alertState]) => {
      if (user == null) {
        return
      }

      const alertLockRequest = {
        alert_ids: alertState.window.windows,
        group_index: alertState.paused.paused ? -1 : alertState.window.currentWindowIndex,
        user: {
          id: user.id,
          username: user.username
        }
      };
      this.socketService.requestOtherLocks(alertLockRequest);
    })
  ), { dispatch: false });

  releaseUnusedGrantedLocks$ = createEffect(() => this.actions$.pipe(
    ofType(AlertLockActions.setLocks),
    withLatestFrom(this.store$.select(selectWindow)),
    map(([action, window]) => ({
      window,
      grantedIds: action.locks.filter((lock) => lock.userID === action.userId).map((lock) => lock.alertID)
    })),
    // The setLocks reducer kept the operator's current group, so none of the granted locks are in view.
    filter(({ window, grantedIds }) => window.alerts.length > 0 && grantedIds.length > 0 && !grantedIds.some((alertId) => window.alerts.includes(alertId))),
    map(({ grantedIds }) => AlertLockActions.releaseLocks({ ids: grantedIds }))
  ));

  releaseLocks$ = createEffect(() => this.actions$.pipe(
    ofType(AlertLockActions.releaseLocks),
    withLatestFrom(
      this.store$.select(selectLoggedInUser),
      this.store$.select(selectLocks),
      this.store$.select(selectLocksToRelease),
      this.store$.select(selectWindow),
    ),
    map(([action, user, locks, locksToRelease, window]) => {

      const idsToRelease = [...locks.values()].filter(alertLock => {
        const passedInIdsHaveLocks = action.ids.includes(alertLock.alertID);
        const userOwnsLock = alertLock.userID == user.id;
        const notInActiveWindow = !window.alerts.includes(alertLock.alertID);
        return userOwnsLock && (passedInIdsHaveLocks || notInActiveWindow);
      }).map(e => e.alertID)

      const alertLocksToRelease = window.windows.length > 0 ? locksToRelease.filter((id) => idsToRelease.includes(id)) : locksToRelease;
      const alertIdsToRelease = [...new Set([...idsToRelease, ...alertLocksToRelease])];
      this.socketService.releaseNewAlertLocks(alertIdsToRelease);
      return AlertLockActions.releaseLocksSuccess({ ids: alertIdsToRelease });
    })
  ));

  // region Storage
  // Logic for storing information so that it is available on reloads

  storeWindowConfiguration$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setWindowSettings),
    tap((action) => {
      const obj: IWindowSettings = {
        interval: action.interval,
        size: action.size
      }
      this.localStorageService.setItem(WINDOW_SETTINGS_KEY, JSON.stringify(obj));
    })
  ), { dispatch: false }
  );

  storeKeyboardMode$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setKeyboardMode),
    tap((action) => {
      this.localStorageService.setItem(KEYBOARD_MODE_KEY, JSON.stringify(action.enabled))
    })
  ), { dispatch: false }
  );

  storeVideoAutoplay$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setAlertVideoAutoplay),
    tap((action) => {
      this.localStorageService.setItem(ALERT_VIDEO_AUTOPLAY_KEY, JSON.stringify(action.enabled));
    })
  ), { dispatch: false }
  );

  storeVideoOverlays$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setAlertVideoOverlays),
    tap((action) => {
      this.localStorageService.setItem(ALERT_VIDEO_OVERLAYS_KEY, JSON.stringify(action.enabled));
    })
  ), { dispatch: false }
  );

  storeDismissOnMute$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setDismissOnMute),
    tap((action) => {
      this.localStorageService.setItem(DISMISS_ON_MUTE_KEY, JSON.stringify(action.enabled));
    })
  ), { dispatch: false }
  );

  //endregion

  storePauseAfterAlertAction$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setPauseAfterAlertAction),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([action, loggedInUser]) => {
      if (loggedInUser != null) {
        storePauseAfterAlertAction(loggedInUser, action.enabled);
        return of(AlertActions.setPauseAfterAlertActionSuccess({ enabled: action.enabled }));
      }

      return EMPTY;
    }))
  );

  loadFocusModeSettings$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.loadFocusModeSettings),
    withLatestFrom(this.store$.select(selectUser)),
    switchMap(([, loggedInUser]) => {
      if (loggedInUser != null) {
        const pauseAfterAlertAction = loadPauseAfterAlertAction(loggedInUser);
        return of(AlertActions.loadFocusModeSettingsSuccess({ pauseAfterAlertAction: pauseAfterAlertAction }));
      }
      return EMPTY;
    }))
  );

  resolveIdentityDetails$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.resolveIdentityDetails),
    withLatestFrom(this.store$.select(selectCurrentAlertState)),
    mergeMap(([action, alertState]) => {
      const alert = this.findAlert(alertState, action.alertId);
      if (alert == null) {
        return of(AlertActions.resolveIdentityDetailsFailure({
          alertId: action.alertId,
          detectionId: action.detectionId,
          error: new Error('Alert not found'),
        }));
      }

      const detection = this.findDetection(alert, action.detectionId);
      if (detection == null) {
        return of(AlertActions.resolveIdentityDetailsFailure({
          alertId: action.alertId,
          detectionId: action.detectionId,
          error: new Error('Detection not found'),
        }));
      }

      const pairs = this.buildResolvePairs(detection);
      if (pairs.length === 0) {
        return of(AlertActions.resolveIdentityDetailsSuccess({
          alertId: action.alertId,
          detectionId: action.detectionId,
          identityDetails: [],
        }));
      }

      return this.registryService.resolveEntries({ identity_matches: pairs }).pipe(
        tap(response => this.showResolveToasts(response)),
        map(response => this.mapResolveResponse(response, action.alertId, action.detectionId)),
        catchError(error => {
          this.toastService.showToast('Failed to resolve identity details', ToastType.ERROR);
          return of(AlertActions.resolveIdentityDetailsFailure({
            alertId: action.alertId,
            detectionId: action.detectionId,
            error,
          }));
        })
      );
    })
  ));

  private findAlert(alertState: IAlertState, alertId: string): IAlert | undefined {
    return alertState.alerts.items.find(a => a.id === alertId)
      ?? alertState.window.fullAlerts.find(a => a.id === alertId);
  }

  private findDetection(alert: IAlert, detectionId: string): IAlertDetection | undefined {
    return alert.detections[detectionId];
  }

  private buildResolvePairs(detection: IAlertDetection): IRegistryEntryPair[] {
    const registryIds = detection.plate_registries as string[] | undefined;
    const entryIds = detection.plate_entries as string[] | undefined;
    if (registryIds == null || entryIds == null || registryIds.length === 0 || entryIds.length === 0) {
      return [];
    }

    const pairs: IRegistryEntryPair[] = [];
    for (let i = 0; i < registryIds.length; i++) {
      if (entryIds[i] != null) {
        pairs.push({ registry_id: registryIds[i], entry_id: entryIds[i] });
      }
    }
    return pairs;
  }

  private showResolveToasts(response: IResolveEntriesResponse): void {
    const failures = response.identity_matches.filter(match => match.status !== 'ok');
    if (failures.length > 0) {
      this.toastService.showToast('Some identity entries could not be resolved', ToastType.WARNING);
    }
    if (failures.length === 0 && response.identity_matches.length > 0) {
      this.toastService.showToast('Identity details fetched', ToastType.SUCCESS);
    }
  }

  private mapResolveResponse(
    response: IResolveEntriesResponse,
    alertId: string,
    detectionId: string,
  ): Action {
    return AlertActions.resolveIdentityDetailsSuccess({
      alertId,
      detectionId,
      identityDetails: response.identity_matches as IIdentityDetail[],
    });
  }

  trackSeenAlertIds$ = createEffect(() => this.actions$.pipe(
    ofType(
      AlertActions.loadAlertsSuccess,
      AlertActions.loadAlertPageSuccess,
      AlertActions.addAlert
    ),
    tap((action) => {
      const ids: string[] = [];
      if (action.type === AlertActions.addAlert.type) {
        ids.push(action.alert.id);
      } else if (action.type === AlertActions.loadAlertsSuccess.type) {
        ids.push(...action.alerts.map(a => a.id));
      } else if (action.type === AlertActions.loadAlertPageSuccess.type) {
        ids.push(...action.alerts.map(a => a.id));
      }
      if (ids.length > 0) {
        this.trackSeenAlerts(ids);
      }
    })
  ), { dispatch: false });

  clearSeenAlertIds$ = createEffect(() => this.actions$.pipe(
    ofType(AlertActions.setFilter, AlertActions.loadFilter, AlertActions.clearFilterStore),
    tap(() => {
      this.seenAlertIds = [];
    })
  ), { dispatch: false });

  private trackSeenAlerts(ids: string[]): void {
    const existing = new Set(this.seenAlertIds);
    const updated = [...this.seenAlertIds];
    for (const id of ids) {
      if (!existing.has(id)) {
        existing.add(id);
        updated.push(id);
      }
    }
    const cap = PAGE_SIZE * 5;
    if (updated.length > cap) {
      this.seenAlertIds = updated.slice(updated.length - cap);
    } else {
      this.seenAlertIds = updated;
    }
  }
}
